import React, { useEffect } from "react";
import { folder, Leva, useControls } from "leva";
import {
  useQueryParams,
  BooleanParam,
  StringParam,
  NumberParam,
} from "use-query-params";
import { paperSizeOptions } from "../utils/paperSizeOptions";
// import { useHistory } from "react-router";
import { presets } from "../presets";

const A5_30_sheets_12mm = `?backgroundColour=rgb%28255%2C%20255%2C%20255%2C%201%29&baseColour=%23345823&blockSize=12&brightnessAdjust=-8&contrast=0&cropBottom=0.92&cropLeft=0.13&cropRight=0.87&cropTop=0.16&&paperType=A5_portrait&pixelsWide=72&preset=topLeft&removeLightestColour=0&showInfo=1&showSheets=0&totalColours=16`;

export const defaultValsPath = A5_30_sheets_12mm;
const presetLabels = presets.map((preset) => preset.name);

export default function Controls({ showControls = true, onChange }) {
  const [query, setQuery] = useQueryParams({
    showInfo: BooleanParam,
    useCustomHues: BooleanParam,
    outputType: StringParam,
    showSheets: BooleanParam,
    removeLightestColour: BooleanParam,
    countLightestColour: BooleanParam,
    paperType: StringParam,
    baseColour: StringParam,
    backgroundColour: StringParam,
    blockSize: NumberParam,
    brightnessAdjust: NumberParam,
    sharpnessAdjust: NumberParam,
    contrast: NumberParam,
    pixelsWide: NumberParam,
    totalColours: NumberParam,
    cropTop: NumberParam,
    cropLeft: NumberParam,
    cropRight: NumberParam,
    cropBottom: NumberParam,
    preset: StringParam,
  });

  const [values, set] = useControls(() => ({
    preset: {
      value: presetLabels[0],
      options: presetLabels,
      onChange: (option) => setQuery({ preset: option }),
    },

    view: folder(
      {
        showInfo: {
          value: false,
          onChange: (value) => setQuery({ showInfo: value }),
        },
        showSheets: {
          value: false,
          onChange: (value) => setQuery({ showSheets: value }),
        },
        outputType: {
          value: "paintFinished",
          options: [
            "stickerCards",
            "stickersFinished",
            "paintCards",
            "paintFinished",
          ],
          onChange: (option) => setQuery({ outputType: option }),
        },
      },
      {
        collapsed: false,
      }
    ),

    sizes: folder(
      {
        paperType: {
          value: paperSizeOptions[2].value,
          options: paperSizeOptions.map((opt) => opt.value),
          onChange: (option) => setQuery({ paperType: option }),
        },

        blockSize: {
          value: 12,
          step: 1,
          min: 1,
          max: 100,
          onChange: (value) => setQuery({ blockSize: value }),
        },
        pixelsWide: {
          value: 72,
          step: 1,
          min: 8,
          max: 1000,
          onChange: (value) => setQuery({ pixelsWide: value }),
        },
      },
      {
        collapsed: true,
      }
    ),

    colours: folder(
      {
        totalColours: {
          value: 10,
          step: 1,
          min: 1,
          max: 50,
          onChange: (value) => setQuery({ totalColours: value }),
        },

        removeLightestColour: {
          value: false,
          onChange: (value) => setQuery({ removeLightestColour: value }),
        },

        countLightestColour: {
          value: false,
          onChange: (value) => setQuery({ countLightestColour: value }),
        },

        useCustomHues: {
          value: false,
          onChange: (value) => setQuery({ useCustomHues: value }),
        },

        baseColour: {
          value: "#862c1c",
          onChange: (value) => setQuery({ baseColour: value }),
        },

        backgroundColour: {
          value: { r: 255, g: 255, b: 255, a: 1 },
          onChange: (value) => {
            const c = value;
            setQuery({
              backgroundColour: `rgb(${c.r}, ${c.g}, ${c.b}, ${c.a})`,
            });
          },
        },
      },
      {
        collapsed: true,
      }
    ),

    cropping: folder(
      {
        cropLeft: {
          value: 0,
          min: 0,
          max: 1,
          onChange: (value) => setQuery({ cropLeft: value }),
        },
        cropTop: {
          value: 0,
          min: 0,
          max: 1,
          onChange: (value) => setQuery({ cropTop: value }),
        },
        cropRight: {
          value: 1,
          min: 0,
          max: 1,
          onChange: (value) => setQuery({ cropRight: value }),
        },
        cropBottom: {
          value: 1,
          min: 0,
          max: 1,
          onChange: (value) => setQuery({ cropBottom: value }),
        },
      },
      {
        collapsed: true,
      }
    ),

    imageAdjustments: folder(
      {
        sharpnessAdjust: {
          value: 0,
          step: 1,
          min: -10,
          max: 10,
          onChange: (value) => setQuery({ sharpnessAdjust: value }),
        },

        brightnessAdjust: {
          value: 0,
          step: 1,
          min: -100,
          max: 100,
          onChange: (value) => setQuery({ brightnessAdjust: value }),
        },

        contrast: {
          value: 0,
          step: 1,
          min: -100,
          max: 100,
          onChange: (value) => setQuery({ contrast: value }),
        },
      },
      {
        collapsed: true,
      }
    ),
  }));

  useEffect(() => {
    const updatedKeys = Object.keys(query);
    if (updatedKeys.length > 0) {
      const updates = {};

      for (let key of updatedKeys) {
        updates[key] = query[key];
      }

      // set the controls based on the query
      set(updates);

      // update the app based on the query
      onChange({ ...values, ...updates });
    }

    // eslint-disable-next-line
  }, [query]);

  return <Leva hidden={!showControls} />;
}
